import { filterDOMProps } from '@react-aria/utils';
import type { DOMProps } from '@react-types/shared';
import type { ReactNode } from 'react';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ContextValue } from 'react-aria-components';
import { useSlottedContext } from 'react-aria-components';

import { getDisplayName, isChainKey } from '@infinex/chain-config';
import type { ChainKey } from '@infinex/chain-config';
import { CHAIN_SVG_REFS } from '@infinex/ui-assets';
import {
  styleVars,
  cn,
  useStyleProps,
  useContextProps,
} from '@infinex/ui-styles';
import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';

import type { EmblemSize } from './constants';
import {
  ACTIVITY_METADATA,
  EMBLEM_DIMENSIONS,
  FALLBACK_DATA_URI,
  SWIDGE_CLIP_ID,
  SWIDGE_FROM_DIAMETER_RATIO,
  SWIDGE_TO_DIAMETER_RATIO,
} from './constants';
import { EmblemGroupContext } from './EmblemGroup';

type ActivityDiscriminant =
  | {
      /** The activity associated with the emblem, if any. */
      activity?: Exclude<
        keyof typeof ACTIVITY_METADATA,
        'receive' | 'swidge' | 'inx'
      >;
    }
  | {
      /** The activity associated with the emblem, if any. */
      activity: 'swidge';
      /** The swidge "from" asset image source URL. */
      fromSrc: string;
    }
  // NOTE: 'receive'/'inx' optionally render a from→to asset pair, e.g. for
  // the INX option-exercise activities
  | {
      /** The activity associated with the emblem, if any. */
      activity: 'receive' | 'inx';
      /** The "from" asset image source URL. */
      fromSrc?: string;
    };

export type EmblemProps = {
  /** The image source URL. If not provided, a fallback image will be displayed. */
  src?: string;
  /**
   * Provide `alt` text for screen readers. Set `alt=""` to indicate that the
   * image is decorative or redundant with displayed text and should not be
   * announced by screen readers.
   */
  alt?: string;
  /**
   * The size of the emblem.
   *
   * @default medium
   */
  size?: EmblemSize;
  /**
   * Use when an asset is available on multiple chains to indicate EITHER a specific
   * chain associated with the asset OR the number of chains if the asset is multi-chain.
   */
  chain?: ChainKey | number;
  /**
   * The asset variant influences the shape of the emblem, indicating the type
   * of asset represented at-a-glance.
   *
   * @default token
   */
  asset?: 'token' | 'nft' | 'nft-collection';
  /** Additional class name(s) applied to the activity adornment. */
  adornmentClassName?: string;
} & DOMProps &
  BaseStyleProps &
  UnsafeStyleProps &
  ActivityDiscriminant;

type EmblemContextValue = EmblemProps & {
  /**
   * Wrap the element with some custom behaviour, within a slotted component.
   */
  render?: (element: ReactNode) => ReactNode;
};

export const EmblemContext = createContext<
  ContextValue<EmblemContextValue, HTMLDivElement>
>({});

/**
 * An emblem is a symbolic image that serves as a graphic representation of an
 * asset, like a token or NFT.
 *
 * [Taurus docs: `Emblem`](https://ui-sb.app.infinex.dev/?path=/docs/components-emblem--docs)
 */
export const Emblem = forwardRef<HTMLDivElement, EmblemProps>(
  function Emblem(props, ref) {
    [props, ref] = useContextProps(props, ref, EmblemContext);
    const groupCtx = useContext(EmblemGroupContext);
    const ctx = useSlottedContext(EmblemContext);
    const { className, style } = useStyleProps(props);

    const [fallbackSrc, setFallbackSrc] = useState<string | null>(null);
    const [fallbackFromSrc, setFallbackFromSrc] = useState<string | null>(null);
    const [srcSuccess, setSrcSuccess] = useState(false);
    const [fromSrcSuccess, setFromSrcSuccess] = useState(false);

    const {
      activity,
      adornmentClassName,
      alt,
      asset = 'token',
      chain,
      size = groupCtx?.size ?? 'medium',
      src = FALLBACK_DATA_URI.value[asset],
      ...otherProps
    } = props;

    const dimensions = EMBLEM_DIMENSIONS[size];
    const isNft = asset === 'nft' || asset === 'nft-collection';
    const isSmall = size === 'small' || size === 'xsmall';

    // typing this would be awkward/complex and make the component less
    // portable. prefer simple runtime errors to defensive render guards.
    if (activity && chain) {
      throw new Error(`Emblems support "activity" or "chain", not both.`);
    }
    if (activity && isSmall) {
      throw new Error(`Small emblems do not support "activity".`);
    }
    if (chain && isSmall) {
      throw new Error(`Small emblems do not support "chain".`);
    }

    if (groupCtx && activity) {
      console.warn('Emblems within a group do not support "activity".');
    }
    if (groupCtx && chain) {
      console.warn('Emblems within a group do not support "chain".');
    }
    if (props.alt == null) {
      console.warn(
        'The `alt` prop was not provided to an emblem. ' +
          'Add `alt` text for screen readers, or set `alt=""` prop to indicate that the emblem ' +
          'is decorative or redundant with displayed text and should not be announced by screen readers.'
      );
    }

    const altText = useMemo(() => {
      if (alt) {
        if (activity) {
          return `${alt} (${ACTIVITY_METADATA[activity].name})`;
        }
        if (chain) {
          const isMultiChain = !isChainKey(chain);
          return isMultiChain
            ? `${alt} (${chain} networks)`
            : `${alt} (${getDisplayName(chain)})`;
        }
      }

      return alt;
    }, [activity, alt, chain]);

    const adornment = useMemo(() => {
      if (groupCtx) return null;

      if (activity) {
        return (
          <ActivityAdornment
            activity={activity}
            size={size}
            adornmentClassName={adornmentClassName}
          />
        );
      }

      if (chain) {
        return <ChainAdornment chain={chain} size={size} />;
      }
    }, [groupCtx, activity, chain, size, adornmentClassName]);

    const clipPathId = useMemo(() => {
      if (groupCtx) return undefined;
      if (activity) return dimensions.activityClipId;
      if (chain) return dimensions.chainClipId;
    }, [groupCtx, activity, chain, dimensions]);

    const handleError = useCallback(
      (setter: (src: string) => void) => () => {
        setter(FALLBACK_DATA_URI.value[asset]);
      },
      [asset]
    );

    const element = (
      <div
        {...filterDOMProps(otherProps)}
        ref={ref}
        className={cn('relative size-(--size) shrink-0 select-none', className)}
        style={styleVars({
          '--size': `${dimensions.size}px`,
          '--radius': isNft ? `${dimensions.radius}px` : '9999px',
          '--swidge-size-to': `round(calc(var(--size) * ${SWIDGE_TO_DIAMETER_RATIO}), 1px)`,
          '--swidge-size-from': `round(calc(var(--size) * ${SWIDGE_FROM_DIAMETER_RATIO}), 1px)`,
          ...style,
        })}
      >
        {asset === 'nft-collection' && (
          // The "fold/slide out" album cover effect to indicate an NFT collection
          <span
            className={cn(
              'absolute inset-0',
              'origin-bottom-right rotate-12',
              'rounded-(--radius)'
            )}
            // medium grey at 50% opacity should work on most backgrounds
            style={{ backgroundColor: '#666', opacity: 0.5 }}
          />
        )}

        {/* The swidge "from" asset, complements its "to" asset. */}
        {isFromSrcAllowed(props) && (
          <div
            className="absolute inset-0"
            style={{ clipPath: `url(#${SWIDGE_CLIP_ID})` }}
          >
            <div className="bg-placeholder absolute bottom-0 left-0 overflow-clip rounded-full">
              <img
                src={fallbackFromSrc ?? props.fromSrc}
                alt=""
                className={cn(
                  'object-cover opacity-0 transition-opacity duration-75',
                  'size-(--swidge-size-from)',
                  { 'bg-white': fromSrcSuccess },
                  // fade the image in after it's loaded
                  { 'opacity-100': fromSrcSuccess || fallbackFromSrc }
                )}
                onError={handleError(setFallbackFromSrc)}
                onLoad={(event) => {
                  if (event.target instanceof HTMLImageElement) {
                    setFromSrcSuccess(
                      event.target.currentSrc !== fallbackFromSrc
                    );
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* The main asset (for swidge this is the "to" portion). */}
        <div
          className="size-full"
          style={styleVars({
            clipPath: clipPathId && `url(#${clipPathId})`,
            '--emblem-source-size': isFromSrcAllowed(props)
              ? 'var(--swidge-size-to)'
              : '100%',
          })}
        >
          <div
            className={cn(
              // Absolutely position top/right for swidge, which has a "from"
              // asset that's rendered bottom/left. The right alignment only
              // affects swidge which has a smaller source size, otherwise the
              // source will fill the available space appropriately.
              'bg-placeholder absolute top-0 right-0 overflow-clip',
              'size-(--emblem-source-size) rounded-(--radius)',
              '2dppx:[--res-px:0.5px] 3dppx:[--res-px:0.33px] 4dppx:[--res-px:0.25px] [--res-px:1px]'
            )}
            style={
              isNft
                ? undefined
                : styleVars({
                    // 1. Clip the image slightly inset to remove any background
                    //    left over from transparent + circular asset sources that
                    //    don't completely fill the area.
                    //    Use feathering to soften jagged edges from the mask.
                    maskImage: `radial-gradient(circle, white calc(var(--circle-edge) - var(--res-px)), transparent var(--circle-edge))`,
                    maskMode: 'alpha',
                    // By default, a radial gradient's size in CSS is calculated
                    // from the center point to the farthest corner.
                    // For this gradient however, we need the distance from the
                    // center point to the farthest edge. Basically the inverse
                    // of the "largest square within a circle" problem, rather
                    // than (d*√2) we're doing (l/√2).
                    // The value is then rounded to align with the nearest
                    // pixel, depending on device resolution.
                    '--circle-edge':
                      'round(calc(100% / sqrt(2)), var(--res-px))',
                  })
            }
          >
            <img
              alt={altText}
              onError={handleError(setFallbackSrc)}
              onLoad={(event) => {
                if (event.target instanceof HTMLImageElement) {
                  setSrcSuccess(event.target.currentSrc !== fallbackSrc);
                }
              }}
              src={fallbackSrc ?? src}
              // 2. set height/width to account for tiny images
              // 3. force the source to "cover" the area, accounts images with
              //    non-square dimensions w/o distorting their aspect ratio
              className={cn(
                'size-full object-cover opacity-0 transition-opacity duration-75',
                {
                  // 4. The white background accounts for token images with
                  //    transparency, which is only applied after the image has
                  //    loaded successfully.
                  //    NFTs _should_ always be completely opaque, so we don't add the
                  //    background colour and can avoid masking (point 1, above).
                  'bg-white': !isNft && srcSuccess,
                  // fade the image in after it's loaded
                  'opacity-100': srcSuccess || fallbackSrc,
                }
              )}
              style={{
                transform: 'scale(1.01)',
              }}
            />
          </div>
        </div>

        {adornment}
      </div>
    );

    if (ctx?.render) {
      return ctx.render(element);
    }

    return element;
  }
);

// Utils
// ----------------------------------------------------------------------------

type WithFromSrc = { activity: 'receive' | 'swidge' | 'inx'; fromSrc: string };
function isFromSrcAllowed(props: EmblemProps): props is WithFromSrc {
  return (
    (props.activity === 'receive' ||
      props.activity === 'swidge' ||
      props.activity === 'inx') &&
    !!props.fromSrc
  );
}

// Adornments
// ----------------------------------------------------------------------------

function ActivityAdornment(
  props: Required<Pick<EmblemProps, 'activity' | 'size'>> &
    Pick<EmblemProps, 'adornmentClassName'>
) {
  const dimensions = EMBLEM_DIMENSIONS[props.size];

  return (
    <div
      className={cn(
        'bg-canvasInverse text-inverse absolute overflow-clip',
        props.adornmentClassName,
        '[&_svg]:size-full'
      )}
      style={{
        borderRadius: dimensions.activityRect.radius,
        top: dimensions.activityRect.top,
        left: dimensions.activityRect.left,
        height: dimensions.activityRect.height,
        width: dimensions.activityRect.width,
      }}
    >
      {ACTIVITY_METADATA[props.activity].glyph}
    </div>
  );
}

function ChainAdornment(props: Required<Pick<EmblemProps, 'chain' | 'size'>>) {
  const { chain, size } = props;
  const dimensions = EMBLEM_DIMENSIONS[size];
  const isMultiChain = !isChainKey(chain);

  return (
    <div
      className={cn('absolute overflow-clip [&_svg]:size-full', {
        'border-emphasis flex items-center justify-center border-2':
          isMultiChain,
      })}
      style={{
        borderRadius: dimensions.chainRect.radius,
        top: dimensions.chainRect.top,
        left: dimensions.chainRect.left,
        height: dimensions.chainRect.height,
        width: dimensions.chainRect.width,
      }}
    >
      {isMultiChain ? (
        <div
          className={cn('text-secondary font-semibold', {
            'text-xs': size === 'medium',
            'text-sm': size === 'large',
            'text-base': size === 'xlarge',
          })}
        >
          {chain}
        </div>
      ) : (
        CHAIN_SVG_REFS[chain].emblemRef
      )}
    </div>
  );
}
